// Composant pour activer les Push Notifications - Version robuste
import React from 'react';
import { motion } from 'framer-motion';
import { Bell, BellOff, Loader2, Check, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import usePushNotifications from '../hooks/usePushNotifications';
import { api } from '../services/api';
import { toast } from 'sonner';

export const NotificationPrompt = ({ customerId, onClose, compact = false }) => {
  const { 
    permission, 
    loading, 
    supported, 
    isEnabled, 
    isDenied,
    requestPermission 
  } = usePushNotifications(customerId);

  const handleEnable = async () => {
    const success = await requestPermission();
    if (success && onClose) {
      setTimeout(onClose, 1500);
    }
  };

  if (!supported) {
    return null;
  }

  if (isEnabled) {
    if (compact) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-2 p-3 rounded-xl bg-green-500/20 border border-green-500/50 text-green-400"
      >
        <Check className="w-5 h-5" />
        <span className="text-sm">Notifications activées</span>
      </motion.div>
    );
  }

  if (isDenied) {
    if (compact) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-2 p-3 rounded-xl bg-red-500/20 border border-red-500/50 text-red-400"
      >
        <BellOff className="w-5 h-5" />
        <div className="text-sm">
          <p>Notifications bloquées</p>
          <p className="text-xs opacity-70">Allez dans les paramètres du navigateur pour les autoriser</p>
        </div>
      </motion.div>
    );
  }

  if (compact) {
    return (
      <Button
        onClick={handleEnable}
        disabled={loading}
        variant="outline"
        size="sm"
        className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <Bell className="w-4 h-4 mr-2" />
            Activer les notifs
          </>
        )}
      </Button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="p-4 rounded-2xl border-2"
      style={{
        background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.1) 0%, rgba(255, 0, 255, 0.05) 100%)',
        borderColor: 'rgba(0, 255, 255, 0.5)',
        boxShadow: '0 0 30px rgba(0, 255, 255, 0.2)'
      }}
    >
      <div className="flex items-start gap-4">
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            rotate: [0, 10, -10, 0]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="p-3 rounded-full bg-cyan-500/20"
        >
          <Bell className="w-8 h-8 text-cyan-400" />
        </motion.div>
        
        <div className="flex-1">
          <h3 className="text-lg font-bold text-white mb-1">
            🔔 Activez les notifications !
          </h3>
          <p className="text-gray-400 text-sm mb-3">
            Recevez des alertes pour :
          </p>
          <ul className="text-sm text-gray-300 space-y-1 mb-4">
            <li>💬 Nouveaux messages du magasin</li>
            <li>🚚 Statut de vos livraisons</li>
            <li>⚡ Offres flash exclusives</li>
            <li>🎂 Votre cadeau anniversaire</li>
            <li>🏆 Récompenses à récupérer</li>
          </ul>
          
          <div className="flex gap-2">
            <Button
              onClick={handleEnable}
              disabled={loading}
              className="flex-1"
              style={{
                background: 'linear-gradient(135deg, #00FFFF 0%, #00FF88 100%)',
                color: '#000'
              }}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Bell className="w-5 h-5 mr-2" />
                  Activer
                </>
              )}
            </Button>
            {onClose && (
              <Button
                onClick={onClose}
                variant="ghost"
                className="text-gray-400 hover:text-white"
              >
                Plus tard
              </Button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Mini bouton pour afficher dans le header - TOUJOURS CLIQUABLE
export const NotificationToggle = ({ customerId }) => {
  const { isEnabled, isDenied, loading, supported, requestPermission } = usePushNotifications(customerId);
  const [showMenu, setShowMenu] = React.useState(false);

  // Si pas supporté, ne rien afficher
  if (!supported) {
    return null;
  }

  // Désactiver les notifications
  const handleDisable = async () => {
    try {
      // Désactiver côté serveur
      if (customerId) {
        await api.savePushToken(customerId, null); // Envoyer null pour désactiver
      }
      
      // On ne peut pas vraiment "révoquer" la permission du navigateur,
      // mais on peut informer l'utilisateur
      toast.success('🔕 Notifications désactivées', {
        description: 'Vous ne recevrez plus de notifications push'
      });
      
      setShowMenu(false);
      
      // Recharger pour mettre à jour l'état
      window.location.reload();
    } catch (error) {
      console.error('Erreur désactivation:', error);
      toast.error('Erreur lors de la désactivation');
    }
  };

  // Si bloqué, afficher l'icône rouge
  if (isDenied) {
    return (
      <button
        onClick={() => {
          toast.error('Notifications bloquées', {
            description: 'Allez dans les paramètres du navigateur pour les autoriser'
          });
        }}
        className="p-2 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
        title="Notifications bloquées - Cliquez pour plus d'infos"
      >
        <BellOff className="w-5 h-5" />
      </button>
    );
  }

  // Si activé, montrer menu pour désactiver
  if (isEnabled) {
    return (
      <div className="relative">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="p-2 rounded-full bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-all"
          title="Notifications activées - Cliquez pour gérer"
        >
          <Bell className="w-5 h-5" />
        </button>
        
        {showMenu && (
          <>
            {/* Overlay pour fermer le menu */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowMenu(false)}
            />
            
            {/* Menu dropdown */}
            <div className="absolute right-0 top-full mt-2 z-50 w-56 rounded-xl overflow-hidden shadow-xl border border-white/10"
              style={{ background: 'rgba(20, 0, 40, 0.95)' }}
            >
              <div className="p-3 border-b border-white/10">
                <div className="flex items-center gap-2 text-green-400">
                  <Check className="w-4 h-4" />
                  <span className="text-sm font-medium">Notifications activées</span>
                </div>
              </div>
              <button
                onClick={handleDisable}
                className="w-full p-3 text-left text-sm text-red-400 hover:bg-red-500/20 transition-all flex items-center gap-2"
              >
                <BellOff className="w-4 h-4" />
                Désactiver les notifications
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Bouton pour activer
  return (
    <button
      onClick={requestPermission}
      disabled={loading}
      className="p-2 rounded-full bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 animate-pulse transition-all cursor-pointer"
      title="Cliquez pour activer les notifications"
    >
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <BellOff className="w-5 h-5" />
      )}
    </button>
  );
};

export default NotificationPrompt;
